import amplitude from 'amplitude-js'
import { APP_NAME } from 'app-config'

import { getMobileOperatingSystem } from 'helpers/getMobileOperatingSystem'
import { getOperatingSystemVersion } from 'helpers/getOperatingSystemVersion'

import {
  IEventLogger,
  TCommonEventProperties,
  TDynamicTextsEventProperties,
} from 'models/events.model'

import { Events } from 'services/eventLogger.service'

import { EventLoggerInstanceName } from 'root-constants'
import { TAppName } from 'root-types'

interface IAmplitude {
  getInstance: (amplitudeInstanceName?: string) => typeof amplitude
  init: (
    apiKey: string,
    userId: string | null,
    options?: Record<string, unknown>,
  ) => void
  setUserId: (userId: string) => void
  setUserProperties: (properties: Record<string, string>) => void
  logEvent: (
    event: string,
    eventProperty?: Record<string, unknown>,
    cb?: () => void,
  ) => void
  setTransport: (transport: string) => void
  sendEvents: () => void
}

export class Amplitude implements IEventLogger {
  public readonly name: EventLoggerInstanceName
  private readonly amplitude: IAmplitude
  private utmTags: Record<string, string>
  private cohort = ''
  private gbSegments: string[] = []
  private dynamicTexts: TDynamicTextsEventProperties = {}
  private userProperties: Record<string, string> = {}
  private readonly isTestEnvironment: boolean
  private readonly isDevEnvironment: boolean
  private readonly trackSegments: boolean
  private readonly appName: TAppName

  constructor({
    instanceName,
    apiKey,
    userId,
    utmTags,
    isTestEnvironment,
    isDevEnvironment,
    trackSegments = false,
    dynamicTexts = {},
    options,
  }: {
    instanceName: EventLoggerInstanceName
    apiKey: string
    userId: string
    utmTags: Record<string, string>
    isTestEnvironment: boolean
    isDevEnvironment: boolean
    trackSegments?: boolean
    dynamicTexts?: TDynamicTextsEventProperties
    options?: Record<string, unknown>
  }) {
    this.utmTags = utmTags
    this.name = instanceName
    this.amplitude = amplitude.getInstance(instanceName)
    this.amplitude.init(apiKey, userId || null, options)
    this.isTestEnvironment = isTestEnvironment
    this.isDevEnvironment = isDevEnvironment
    this.trackSegments = trackSegments
    this.dynamicTexts = dynamicTexts
    this.appName = APP_NAME
  }

  log(
    event: string,
    eventProperty?: Record<string, unknown>,
    cb?: () => void,
  ): void {
    const isSessionStart = event === Events.SESSION_STARTED

    const mergedEventProperty: TCommonEventProperties = {
      cohort: this.cohort,
      attribution: this.utmTags,
      is_test_environment: this.isTestEnvironment,
      is_dev_environment: this.isDevEnvironment,
      operating_system_version: getOperatingSystemVersion(),
      ...(this.trackSegments && { segment_names: this.gbSegments }),
      ...(!isSessionStart && this.dynamicTexts),
      ...eventProperty,
    }

    this.amplitude.logEvent(event, mergedEventProperty, cb)
  }

  configure({ cohort }: { cohort: string }): void {
    this.cohort = cohort
    this.setUserProperties(cohort)
  }

  flushWithBeacon(): void {
    this.amplitude.setTransport('beacon')
    this.amplitude.sendEvents()
  }

  updateGbSegments(segments: string[]): void {
    this.gbSegments = segments
  }

  updateDynamicTexts(texts: TDynamicTextsEventProperties): void {
    this.dynamicTexts = texts
  }

  updateUserProperties(
    stripeAccountId: string,
    stripeAccountName: string,
  ): void {
    const updatedUserProperties = {
      ...this.userProperties,
      stripe_account_id: stripeAccountId,
      stripe_account_name: stripeAccountName,
    }
    this.userProperties = updatedUserProperties
    this.amplitude.setUserProperties(updatedUserProperties)
  }

  private setUserProperties(cohort: string): void {
    const userProperties = {
      'Device Type': getMobileOperatingSystem(),
      'Operating System Version': getOperatingSystemVersion(),
      cohort_name: cohort,
      app_name: this.appName,
    }
    this.userProperties = userProperties
    this.amplitude.setUserProperties(userProperties)
  }
}
